export type * from "@elaso/db";

import type { Prisma, Workspace } from "@elaso/db";

export type CompleteMessage = Prisma.MessageGetPayload<{
	include: {
		reactions: true;
		referencedMessage: true;
		attachments: true;
	};
}> & {
	isPending?: boolean;
	hasFailed?: boolean;
	tempId?: string;
};

export type ActivityStatus = "active" | "idle";

/**
 * Presence reaches every connection in the workspace several times a second, so
 * a field nothing reads is paid for on every flush. Two were dropped in
 * TECH-3157. `id` duplicated `firebaseUid`, and the client's nested-key config
 * keys on `firebaseUid`, so nothing read it. `lastActivityTime` was never read,
 * because the client's same-named field is a local number for idle detection,
 * populated from `Date.now()` rather than from this payload.
 *
 * `lastChatTime` looks equally dead and is NOT. The attendance table's last-chat
 * column falls back to it when no attendance data is loaded. Removing it blanks
 * that column. See TECH-3157 finding 3 for the ordering that change needs.
 *
 * The offline path still sends `{ id: firebaseUid }`, but as a `remove` payload
 * where `id` is the map key the client deletes by, unrelated to the field
 * removed here.
 */
export type UserPresence = {
	firebaseUid: string;
	status: "online" | "offline";
	activityStatus?: ActivityStatus; // active = user interacting, idle = no interaction for 5 min
	currentChannelId?: string | null; // Channel ID the user is currently in
	lastChatTime?: string | null; // ISO timestamp of last chat message sent
	isMicrophoneEnabled?: boolean; // Whether user's microphone is enabled in voice channel
	isCameraEnabled?: boolean; // Whether user's camera is enabled in voice channel
	profile?: import("../schemas/profile").UserProfile; // Included in presence messages so clients can populate user entries without waiting for users.list
};

export type AttendanceSessionStatus = "pending" | "active" | "completed";

export type ChannelType = "text" | "voice_and_text" | "stage";

export type AttendanceSession = {
	id: string;
	name: string;
	status: AttendanceSessionStatus;
	createdBy: string;
	startedAt: string | null;
	endedAt: string | null;
	createdAt: string;
	updatedAt: string;
};

export type Category = {
	id: string;
	name: string;
	workspaceId: string;
	order: number;
	isVisible: boolean;
	maxParticipants: number | null;
	createdAt: Date;
	updatedAt: Date;
};

export type WorkspaceScheduleSettings = {
	scheduleTitle: string | null;
	fullScheduleRoleKeys: string[];
};

export const DEFAULT_WORKSPACE_SCHEDULE_SETTINGS: WorkspaceScheduleSettings = {
	scheduleTitle: "Schedule",
	fullScheduleRoleKeys: [],
};

/**
 * A workspace row as the ELASO Core server sends it: on `workspace.list`,
 * `workspace.get`, `workspace.getByProgramId` and the `workspace.update`
 * broadcast.
 *
 * `everyoneKey` is the permission key that stands for "everyone" here, the
 * `workspace#participant` userset carrying baseline access
 * (docs/adr/11-workspace-participation-permission-structure.md). Both clients
 * present "everyone" as a role to pick, and both used to spell that key
 * themselves from their own copy of the deployment name. The server sends it
 * now, so the key grammar has one home and the rewrite of
 * docs/adr/34-every-key-names-the-owning-organisation.md touches no client
 * (TECH-3336). A client compares this key and hands it back unchanged; it never
 * takes it apart.
 *
 * `scheduleSettings` is narrowed from Prisma's nullable `Json` to the shape the
 * route normalises every row to before it goes out.
 */
export type WorkspaceRow = Omit<Workspace, "scheduleSettings"> & {
	scheduleSettings: WorkspaceScheduleSettings;
	everyoneKey: string;
	hostOrganization: { id: string; name: string } | null;
};

export type WorkspaceElsewhereRow = {
	connectionId: string;
	workspaceName: string;
	hostOrganizationName: string;
	hostInstanceUrl: string;
	consentGiven: boolean;
};

/**
 * A named role as the server sends it, on `roles.list` and on the `roles.create`
 * and `roles.rename` broadcasts, and as both clients hold it in their roster.
 *
 * `key` is this role's permission key, from the server's one key builder. It is
 * what a client sends back when granting or revoking the role on a channel or a
 * document, and what it matches a stored key against. Clients read it; they do
 * not build it (TECH-3336, and see {@link WorkspaceRow}).
 *
 * The timestamps are optional and widened to `string`, because they arrive as
 * JSON and several client call sites construct a roster row without them.
 */
export type RoleRow = {
	id: string;
	workspaceId: string;
	name: string;
	key: string;
	createdAt?: string | Date;
	updatedAt?: string | Date;
};

export type MuteDuration = "15m" | "1h" | "8h" | "24h" | "indefinite";
